import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import notification from "@app/services/notification";
import {
    stockKanbanContainersServices,
    type StockKanbanContainer,
    type StockKanbanCreateContainerByScanParams,
    type StockKanbanMoveCompletedSelectionParams,
} from "../../../../api/stockKanbanContainers";

/** Параметры хука мутаций списка контейнеров КАНБАН. */
export interface UseStockKanbanSupplyListMutationsParams {
    /**
     * Колбэк добавления контейнера в список.
     * @param container контейнер после скана / взятия на комплектование
     */
    onContainerCreate?: (container: StockKanbanContainer) => void;
    /** Колбэк синхронизации списка после перемещения. */
    onSelectionMove?: () => void | Promise<unknown>;
}

/** Мутации списка контейнеров КАНБАН. */
export interface UseStockKanbanSupplyListMutationsResult {
    /** Поиск контейнера по скану и взятие на комплектование при необходимости. */
    createByScanMutation: UseMutationResult<StockKanbanContainer, Error, StockKanbanCreateContainerByScanParams>;
    /** Перемещение контейнеров со статусом «комплектование завершено». */
    moveCompletedSelectionMutation: UseMutationResult<void, Error, StockKanbanMoveCompletedSelectionParams>;
}

/** Screen-level мутации списка контейнеров КАНБАН. */
export function useStockKanbanSupplyListMutations({
    onContainerCreate,
    onSelectionMove,
}: UseStockKanbanSupplyListMutationsParams): UseStockKanbanSupplyListMutationsResult {
    const createByScanMutation = useMutation({
        mutationKey: ["stock", "kanbanSupply", "createByScan"],
        meta: { errorSource: "stock.kanbanSupply.createByScan" },
        mutationFn: (variables: StockKanbanCreateContainerByScanParams) =>
            stockKanbanContainersServices.createByScan(variables),
        onSuccess: (container) => {
            notification.success("Контейнер добавлен в выборку");
            onContainerCreate?.(container);
        },
    });

    const moveCompletedSelectionMutation = useMutation({
        mutationKey: ["stock", "kanbanSupply", "moveCompletedSelection"],
        meta: { errorSource: "stock.kanbanSupply.moveCompletedSelection" },
        mutationFn: (variables: StockKanbanMoveCompletedSelectionParams) =>
            stockKanbanContainersServices.moveCompletedSelection(variables),
        onSuccess: async () => {
            notification.success("Перемещение скомплектованных контейнеров выполнено");
            await onSelectionMove?.();
        },
    });

    return {
        createByScanMutation,
        moveCompletedSelectionMutation,
    };
}
